import React, { useEffect, useState } from 'react';
import { useV3Api } from '../../api/useV3Api';
import { filterQuery, sortRules, switchesOf, tickSwitches } from '../../policy/ruleSwitches';
import AddSoftwareBlade from './AddSoftwareBlade';

// One rule per program and four ticks with their names: Install, Update, Remove and On. Nothing ticked allows the program and reports drift only.
// Remove stands alone: ticking it clears Install and Update, and ticking either of those clears Remove. Programs are never typed: Add software (at the
// top) opens a blade that lists what the policy's machines have installed, or searches the winget catalog, with the same ticks on each line.
const SWITCHES = [['install', 'Install'], ['update', 'Update'], ['remove', 'Remove']];
const list = (v) => (Array.isArray(v) ? v : []);

// The friendly name of each rule's program, by its key: from the software the policy's machines report, and for a key that is not there (added from the
// catalog) from the catalog by its id. The key itself is shown until a name is found.
function useProgramNames(account, filter, rules) {
  const { get } = useV3Api();
  const [names, setNames] = useState({});
  const query = filterQuery(filter);
  const keys = rules.map((r) => String(r.key || '').trim()).filter(Boolean);
  const wanted = keys.join('|');

  useEffect(() => {
    if (!account || account === '*') return undefined;
    let cancelled = false;
    get('v3/software', { account, filter: query })
      .then((r) => {
        if (cancelled) return;
        const found = {};
        list(r && r.items).forEach((p) => { if (p.key && p.name) found[p.key.toLowerCase()] = p.name; });
        setNames((n) => ({ ...found, ...n }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [get, account, query]);

  useEffect(() => {
    let cancelled = false;
    keys.filter((k) => !names[k.toLowerCase()] && k.includes('.')).slice(0, 25).forEach((k) => {
      get('v3/packages', { id: k })
        .then((r) => { const p = list(r && r.items)[0]; if (!cancelled && p && p.name) setNames((n) => ({ ...n, [k.toLowerCase()]: p.name })); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [get, wanted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (key) => names[String(key || '').trim().toLowerCase()] || '';
}

export default function RulesField({ value, onChange, field, account, filter }) {
  const rules = value || [];
  const [bladeOpen, setBladeOpen] = useState(false);
  const [order, setOrder] = useState('');                 // the direction the programs were last sorted in: '', 'asc' or 'desc'
  const nameOf = useProgramNames(account, filter, rules);
  const sort = () => { const next = order === 'asc' ? 'desc' : 'asc'; setOrder(next); onChange(sortRules(rules, next)); };
  const set = (index, patch) => onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const tick = (index, name, on) => set(index, tickSwitches(name, on));
  return (
    <div className="repeat">
      <div className="repeat-actions rules-top">
        {account && <button type="button" className="btn btn-primary btn-sm" disabled={rules.length >= (field.max || 200)} onClick={() => setBladeOpen(true)}>Add software</button>}
        {rules.length > 0 && <button type="button" className="rule-sort" aria-label="Sort programs" onClick={sort}>{`${rules.length} program${rules.length === 1 ? '' : 's'}`}{order === 'asc' ? ' ▲' : order === 'desc' ? ' ▼' : ' ⇅'}</button>}
      </div>
      {rules.length === 0 && <div className="help">No software yet. Use Add software.</div>}
      {rules.map((r, i) => {
        const on = switchesOf(r);
        const name = nameOf(r.key);
        return (
          <div className="repeat-row rule-row" key={i}>
            <div className="rule-program" aria-label={`Program ${i + 1}`}>
              <strong>{name || r.key || 'No program'}</strong>
              {name && r.key && <div className="sub mono">{r.key}</div>}
            </div>
            {SWITCHES.map(([sw, label]) => (
              <label className="check rule-switch" key={sw}>
                <input type="checkbox" aria-label={`${label} ${i + 1}`} checked={on[sw]} onChange={(e) => tick(i, sw, e.target.checked)} />{label}
              </label>
            ))}
            <label className="check rule-switch"><input type="checkbox" aria-label={`Enabled ${i + 1}`} checked={r.enabled !== false} onChange={(e) => set(i, { enabled: e.target.checked })} />On</label>
            <button type="button" className="btn btn-ghost btn-sm rule-delete" aria-label={`Remove rule ${i + 1}`} onClick={() => onChange(rules.filter((_, j) => j !== i))}>Delete</button>
          </div>
        );
      })}
      {bladeOpen && <AddSoftwareBlade account={account} filter={filter} rules={rules} onChange={onChange} onClose={() => setBladeOpen(false)} />}
    </div>
  );
}
